import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Paper,
  Grid,
  Avatar,
  CircularProgress,
  Zoom,
} from "@mui/material";
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";

// Helper function to safely get file URL
const getFileURL = (file) => {
  if (!file) return "";
  
  // If file is already a string URL, return it
  if (typeof file === "string") {
    return file;
  }
  
  // If file is a File or Blob object, create object URL
  if (file instanceof File || file instanceof Blob) {
    try {
      return URL.createObjectURL(file);
    } catch (error) {
      console.error("Error creating object URL:", error);
      return "";
    }
  }
  
  // Fallback: return empty string
  console.warn("Invalid file type passed to getFileURL:", file);
  return "";
};

const PreviewPostDialog = ({
  open = false,
  onClose = () => {},
  onConfirm = () => {},
  isSubmitting = false,
  previewData = null,
  socialMediaAccounts = [],
  onOpenImageModal = null,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(75,90,182,0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{ fontWeight: 700, color: "#4B5AB6", textAlign: "center", pb: 1 }}
      >
        Preview Your Post
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {previewData && (
          <Box>
            {/* Platforms */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
              >
                Posting to Platforms:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {previewData.platforms?.map((platform, index) => (
                  <Chip
                    key={index}
                    label={platform}
                    sx={{
                      bgcolor: "#e0e7ff",
                      color: "#4B5AB6",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Selected Accounts */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
              >
                Selected Accounts ({previewData.selectedAccounts?.length || 0}):
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {previewData.selectedAccounts?.map((accountId) => {
                  const account = socialMediaAccounts.find(
                    (acc) => acc.accountId === accountId
                  );
                  return (
                    <Paper
                      key={accountId}
                      sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          sx={{ width: 32, height: 32, bgcolor: "#4B5AB6" }}
                        >
                          {(account?.provider || "A").charAt(0)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {account?.accountName || accountId}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748b" }}
                          >
                            {account?.provider} • {account?.status}
                          </Typography>
                        </Box>
                        <Chip
                          label={account?.provider || "Unknown"}
                          size="small"
                          sx={{ bgcolor: "#e8f5e8", color: "#2e7d32" }}
                        />
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            {/* Content Type */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
              >
                Content Type: {previewData.contentType}
              </Typography>
            </Box>

            {/* Media Preview */}
            {previewData.mediaFile && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
                >
                  Media Files (
                  {Array.isArray(previewData.mediaFile)
                    ? previewData.mediaFile.length
                    : 1}
                  ):
                </Typography>

                <Grid container spacing={2}>
                  {(Array.isArray(previewData.mediaFile)
                    ? previewData.mediaFile
                    : [previewData.mediaFile]
                  ).map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: "#f8fafc",
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          cursor:
                            previewData.contentType === "Image"
                              ? "pointer"
                              : "default",
                          transition: "all 0.2s ease",
                          "&:hover":
                            previewData.contentType === "Image"
                              ? {
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                  borderColor: "#4B5AB6",
                                }
                              : {},
                        }}
                        onClick={() => {
                          if (
                            previewData.contentType === "Image" &&
                            onOpenImageModal
                          ) {
                            const images = Array.isArray(previewData.mediaFile)
                              ? previewData.mediaFile
                              : [previewData.mediaFile];
                            onOpenImageModal(images, index);
                          }
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          {/* Media Preview Container */}
                          <Box
                            sx={{
                              width: "100%",
                              borderRadius: 2,
                              overflow: "hidden",
                              bgcolor: "#ffffff",
                              border: "1px solid #d1d5db",
                              position: "relative",
                              minHeight: "120px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {previewData.contentType === "Image" && (
                              <>
                                <img
                                  src={getFileURL(file)}
                                  alt={`Preview ${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: "120px",
                                    objectFit: "contain",
                                    objectPosition: "center",
                                    borderRadius: "8px",
                                    backgroundColor: "#f8fafc",
                                  }}
                                />
                                {/* Overlay for click indication */}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    bgcolor: "rgba(255,255,255,0.6)",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: 24,
                                    height: 24,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "14px",
                                    opacity: 0,
                                    transition: "opacity 0.2s",
                                    ".MuiPaper-root:hover &": { opacity: 1 },
                                  }}
                                >
                                  <Zoom in={true} timeout={300}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      🔍
                                    </Typography>
                                  </Zoom>
                                </Box>
                              </>
                            )}

                            {previewData.contentType === "Video" && (
                              <video
                                src={getFileURL(file)}
                                controls
                                style={{
                                  width: "100%",
                                  height: "120px",
                                  borderRadius: "8px",
                                  objectFit: "contain",
                                }}
                              />
                            )}
                          </Box>

                          {/* File Information */}
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#1e293b",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                mb: 0.5,
                                wordBreak: "break-word",
                                lineHeight: 1.2,
                              }}
                              title={`Original: ${
                                file.originalName || file.name
                              }\nUnique: ${file.uniqueName || file.name}`}
                            >
                              {(file.originalName || file.name).length > 20
                                ? `${(file.originalName || file.name).substring(
                                    0,
                                    20
                                  )}...`
                                : file.originalName || file.name}
                            </Typography>

                            {file.uniqueName && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "rgba(34, 197, 94, 0.8)",
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                ✓ Unique name:{" "}
                                {file.uniqueName.length > 25
                                  ? `${file.uniqueName.substring(0, 25)}...`
                                  : file.uniqueName}
                              </Typography>
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 1,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#64748b", fontSize: "0.7rem" }}
                              >
                                {(file.size / 1024 / 1024).toFixed(1)} MB
                              </Typography>
                              <Chip
                                label={file.type.split("/")[1].toUpperCase()}
                                size="small"
                                sx={{
                                  bgcolor: "#dbeafe",
                                  color: "#1e40af",
                                  fontSize: "0.65rem",
                                  height: 18,
                                  fontWeight: 500,
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Post Content */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
              >
                Post Content:
              </Typography>
              <Paper sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#475569",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {previewData.postContent}
                </Typography>
              </Paper>
            </Box>

            {/* Schedule Date (if Run Later) */}
            {previewData.type === "runLater" && previewData.dateTime && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
                >
                  Scheduled for:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#4B5AB6", fontWeight: 600 }}
                >
                  {previewData.dateTime.format("MMMM DD, YYYY at hh:mm A")}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: "#64748b" }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button" // ensure no implicit form submit
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : previewData?.type === "runNow" ? (
              <SendIcon />
            ) : (
              <ScheduleIcon />
            )
          }
          sx={{
            bgcolor: isSubmitting ? "#94a3b8" : "#4B5AB6",
            "&:hover": { bgcolor: isSubmitting ? "#94a3b8" : "#3a4891" },
          }}
        >
          {isSubmitting
            ? "Processing..."
            : `Confirm ${previewData?.type === "runNow" ? "Post" : "Schedule"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewPostDialog;
